import { FC, useState, useCallback, ClipboardEvent, KeyboardEvent, useRef, useMemo } from 'react'
import styles from 'components/atoms/inputs/EditInPlaceInput/EditInPlaceInput.styl'
import { Omit } from 'interfaces'
import { useFormContext, Controller } from 'react-hook-form'
import PencilSvg from 'icons/pencil.svg'
import CheckSvg from 'icons/check.svg'
import SvgIcon from 'components/atoms/SvgIcon'
import { FocusinHandler } from 'handlers/FocusinHandler/FocusinHandler'
import cn from 'classnames'
import { useMutableRef } from 'hooks/useMutableRef'
import { generate } from 'randomstring'

type EditInPlaceInputProps = {
  value: string
  onChange?: (value: string) => void
  maxLength?: number
  withBg?: boolean
  disabled?: boolean
}
export const EditInPlaceInput: FC<EditInPlaceInputProps> = (props) => {
  const { value, onChange, withBg, disabled, maxLength } = props
  const onChangeRef = useMutableRef(onChange)

  const nodeRef = useRef<HTMLDivElement>(null)

  const [isEdit, setIsEdit] = useState(false)

  const handleFocus = useCallback(() => {
    setIsEdit(true)

    const { current } = nodeRef
    if (!current) {
      return
    }

    current.focus()

    const selection = window.getSelection()
    if (!selection) {
      return
    }

    const range = document.createRange()
    range.selectNodeContents(current)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
    current.focus()
  }, [])

  const handleBlur = useCallback(() => {
    setIsEdit(false)

    if (!onChangeRef.current || !nodeRef.current || nodeRef.current.innerText === value) {
      return
    }

    onChangeRef.current(nodeRef.current.innerText)
  }, [onChangeRef, value])

  const handlePaste = useCallback((event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()

    const text = event.clipboardData.getData('text')
    document.execCommand('insertHTML', false, text)
  }, [])

  const MARKER = useMemo(() => `EditInPlaceInput${ generate() }`, [])
  const SELECTOR = useMemo(() => `[data-marker=${ MARKER }][contenteditable], input:not([type=hidden]), button`, [MARKER])
  const focusNext = useCallback(() => {
    const { current } = nodeRef
    if (!current) {
      return
    }
    const items = Array.from(document.querySelectorAll(SELECTOR))
    const index = items.indexOf(current)
    const nextNode = (items[index + 1] || document.body) as HTMLElement
    if (index === -1 || !nextNode) {
      return
    }

    nextNode.focus()
  }, [SELECTOR])

  const handleSave = useCallback(() => {
    const { current } = nodeRef
    if (!current) {
      return
    }

    focusNext()
  }, [focusNext])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const { current } = nodeRef
    const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete'

    if (!current) {
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      handleSave()
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (nodeRef.current) {
        nodeRef.current.innerText = value
      }
      focusNext()
      return
    }

    if (!isDeleteKey && !!maxLength && current.innerText.length > maxLength) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
  }, [focusNext, handleSave, value, maxLength])

  const classNames = cn(styles.editInPlaceInput, {
    [styles.isEdit]: isEdit,
    [styles.withBg]: withBg,
  })

  return (
    <FocusinHandler onFocus={ handleFocus } onBlurCapture={ handleBlur } className={ styles.inputWrapper }>
      {
        disabled ?
          <div>{ value }</div>
          :
          <div
            tabIndex={ 2 }
            ref={ nodeRef }
            className={ classNames }
            contentEditable
            suppressContentEditableWarning
            spellCheck={ false }
            onPaste={ handlePaste }
            onKeyDown={ handleKeyDown }
            data-marker={ MARKER }
          >
            { value }
          </div>
      }
      { isEdit && (
        <span className={ styles.icon } onClick={ handleSave }>
          <SvgIcon src={ CheckSvg } />
        </span>
      )}
      { !isEdit && !disabled && (
        <span className={ styles.icon } onClick={ handleFocus }>
          <SvgIcon src={ PencilSvg } />
        </span>
      )}
    </FocusinHandler>
  )
}

export type EditInPlaceInputControllerProps =
  & Omit<EditInPlaceInputProps, 'value'>
  & {
    name: string
  }
export const EditInPlaceInputController: FC<EditInPlaceInputControllerProps> = (props) => {
  const { name, ...restProps } = props
  const { control, trigger } = useFormContext<Record<string, any>>()
  const triggerRef = useMutableRef(trigger)

  const inputRenderer = useCallback(({ field: { value, onChange }}) => {
    const handle = (nextValue: string) => {
      onChange(nextValue)
      triggerRef.current(name)
    }
    return (
      <EditInPlaceInput
        { ...restProps }
        value={ value }
        onChange={ handle }
      />
    )
  }, [restProps, name, triggerRef])

  return (
    <Controller
      control={ control }
      name={ name }
      render={ inputRenderer }
    />
  )
}
